import type { FetchArgs, FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { baseApi } from "./baseApi";
import { getStoredClientUser } from "../auth/clientSession";
import { ProjectRecord, ProjectStatus, ProjectTaskProgress } from "../../pages/erp/adminData";

export interface ProjectApiResponse<T = unknown> {
  success: boolean;
  message: string;
  data: T;
  errors: string[];
}

export interface ProjectPayload {
  title: string;
  owner: string;
  location: string;
  area: string;
  rateOfConstruction: string;
  description: string;
  status: ProjectStatus;
  contractFiles: string[];
  progressTasks?: ProjectTaskProgress[];
}

export interface ProjectTaskPayload {
  id?: string;
  moduleName: string;
  owner: string;
  plannedPercent: number;
  actualPercent: number;
  plannedEndDate: string;
  actualEndDate?: string;
  notes: string;
}

interface SaveProjectTaskArgs {
  projectId: string;
  task: ProjectTaskPayload;
  projectSnapshot: ProjectRecord;
}

interface DeleteProjectTaskArgs {
  projectId: string;
  taskId: string;
  projectSnapshot: ProjectRecord;
}

export interface ProjectContractFile {
  id: number;
  fileName: string;
  fileUrl: string;
  uploadedAt?: string;
}

const toTrimmedString = (value: unknown) =>
  typeof value === "string" ? value.trim() : "";

const normalizeProjectStatus = (status: unknown) => {
  const normalized = String(status ?? "").trim().toLowerCase();

  if (normalized === "completed" || normalized === "in-progress") {
    return normalized as ProjectStatus;
  }

  return "pending" as ProjectStatus;
};

const toNumber = (value: unknown) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const normalizeTask = (task: Record<string, any>): ProjectTaskProgress => ({
  id: String(task.id ?? task.taskId ?? task.projectTaskId ?? `TSK-${Date.now()}`),
  moduleName: String(task.moduleName ?? task.taskName ?? task.name ?? task.stageName ?? ""),
  owner: String(task.owner ?? task.assignedTo ?? task.teamName ?? task.responsibleTeam ?? ""),
  plannedPercent: toNumber(task.plannedPercent ?? task.plannedProgress ?? task.targetPercent),
  actualPercent: toNumber(task.actualPercent ?? task.progressPercent ?? task.currentProgress),
  plannedEndDate: String(task.plannedEndDate ?? task.targetDate ?? task.expectedEndDate ?? ""),
  actualEndDate: toTrimmedString(task.actualEndDate ?? task.completedDate ?? task.endDate) || undefined,
  notes: String(task.notes ?? task.remarks ?? task.comment ?? task.description ?? ""),
});

const normalizeTaskCollection = (value: unknown): ProjectTaskProgress[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.map((task) => normalizeTask((task ?? {}) as Record<string, any>));
};

const normalizeProjectRecord = (item: Record<string, any>): ProjectRecord => ({
  id: String(item.id ?? item.projectId ?? item.projectID ?? `PRJ-${Date.now()}`),
  title: String(item.title ?? item.projectName ?? item.name ?? ""),
  owner: String(item.owner ?? item.clientName ?? item.projectOwner ?? item.customerName ?? ""),
  ownerEmail: toTrimmedString(item.ownerEmail ?? item.clientEmail ?? item.email) || undefined,
  clientId: toTrimmedString(item.clientId ?? item.customerId ?? item.userId) || undefined,
  clientPhone: toTrimmedString(item.clientPhone ?? item.phone ?? item.mobile) || undefined,
  location: String(item.location ?? item.city ?? item.siteLocation ?? ""),
  area: String(item.area ?? item.siteArea ?? ""),
  rateOfConstruction: String(item.rateOfConstruction ?? item.rate ?? item.cost ?? ""),
  description: String(item.description ?? item.projectDescription ?? ""),
  status: normalizeProjectStatus(item.status),
  contractFiles: Array.isArray(item.contractFiles)
    ? item.contractFiles.map((file: unknown) => String(file))
    : Array.isArray(item.documents)
    ? item.documents.map((file: unknown) => String(file))
    : [],
  createdAt: String(item.createdAt ?? item.createdDate ?? new Date().toISOString().slice(0, 10)),
  progressTasks: normalizeTaskCollection(
    item.progressTasks ?? item.projectProgress ?? item.tasks ?? item.projectTasks
  ),
});

const getProjectItems = (response: ProjectApiResponse<unknown> | unknown) => {
  const data = (response as ProjectApiResponse<unknown>)?.data ?? response;

  if (Array.isArray(data)) {
    return data as Record<string, any>[];
  }

  if (Array.isArray((data as Record<string, unknown> | undefined)?.items)) {
    return (data as { items: Record<string, any>[] }).items;
  }

  if (Array.isArray((data as Record<string, unknown> | undefined)?.projects)) {
    return (data as { projects: Record<string, any>[] }).projects;
  }

  return [] as Record<string, any>[];
};

const normalizeProjectResponse = (response: ProjectApiResponse<unknown> | unknown) => {
  const data = (response as ProjectApiResponse<unknown>)?.data ?? response;
  return normalizeProjectRecord((data ?? {}) as Record<string, any>);
};

const buildProjectPayload = (project: ProjectRecord): ProjectPayload => ({
  title: project.title,
  owner: project.owner,
  location: project.location,
  area: project.area,
  rateOfConstruction: project.rateOfConstruction,
  description: project.description,
  status: project.status,
  contractFiles: project.contractFiles,
  progressTasks: project.progressTasks,
});

const matchesClient = (project: ProjectRecord) => {
  const clientUser = getStoredClientUser();
  if (!clientUser) {
    return false;
  }

  const candidateStrings = [
    project.owner,
    project.ownerEmail,
    project.clientId,
    project.clientPhone,
  ]
    .filter(Boolean)
    .map((value) => String(value).trim().toLowerCase());

  const userStrings = [clientUser.name, clientUser.email, clientUser.id, clientUser.phone]
    .filter(Boolean)
    .map((value) => String(value).trim().toLowerCase());

  return userStrings.some((value) => candidateStrings.includes(value));
};

const attemptQueryChain = async (
  baseQuery: (args: string | FetchArgs) => any,
  queries: FetchArgs[]
) => {
  let lastError: FetchBaseQueryError | undefined;

  for (let index = 0; index < queries.length; index += 1) {
    const result = await Promise.resolve(baseQuery(queries[index]));
    if (!result.error) {
      return { result, index };
    }

    lastError = result.error;
  }

  return { result: { error: lastError as FetchBaseQueryError }, index: -1 };
};

export const projectsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    fetchProjects: builder.query<ProjectRecord[], void>({
      query: () => ({
        url: "/Project",
        method: "GET",
      }),
      transformResponse: (response: ProjectApiResponse<unknown> | unknown) =>
        getProjectItems(response).map((item) => normalizeProjectRecord(item)),
      providesTags: ["Project"],
    }),
    fetchClientProjects: builder.query<ProjectRecord[], void>({
      async queryFn(_arg, _api, _extraOptions, baseQuery) {
        const { result, index } = await attemptQueryChain(baseQuery, [
          { url: "/Client/projects", method: "GET" },
          { url: "/Client/Project", method: "GET" },
          { url: "/Project/client", method: "GET" },
          { url: "/Project/client/my-projects", method: "GET" },
          { url: "/Project/my-projects", method: "GET" },
          { url: "/Project", method: "GET" },
        ]);

        if (result.error) {
          return { error: result.error };
        }

        const projects = getProjectItems(result.data).map((item) => normalizeProjectRecord(item));
        return {
          data: index >= 0 && index < 5 ? projects : projects.filter(matchesClient),
        };
      },
      providesTags: ["Project"],
    }),
    fetchProjectById: builder.query<ProjectRecord, string>({
      query: (id) => ({
        url: `/Project/${id}`,
        method: "GET",
      }),
      transformResponse: (response: ProjectApiResponse<unknown> | unknown) => {
        return normalizeProjectResponse(response);
      },
      providesTags: (_result, _error, id) => [{ type: "Project", id }],
    }),
    createProject: builder.mutation<ProjectApiResponse, ProjectPayload>({
      query: (payload) => ({
        url: "/Project",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["Project"],
    }),
    updateProject: builder.mutation<ProjectApiResponse, { id: string; payload: ProjectPayload }>({
      query: ({ id, payload }) => ({
        url: `/Project/${id}`,
        method: "PUT",
        body: {
          id,
          ...payload,
        },
      }),
      invalidatesTags: (_result, _error, arg) => ["Project", { type: "Project", id: arg.id }],
    }),
    saveProjectTask: builder.mutation<ProjectApiResponse, SaveProjectTaskArgs>({
      async queryFn({ projectId, task, projectSnapshot }, _api, _extraOptions, baseQuery) {
        const taskMethod = task.id ? "PUT" : "POST";
        const taskUrlSuffix = task.id ? `/${task.id}` : "";
        const { result } = await attemptQueryChain(baseQuery, [
          {
            url: `/Project/${projectId}/progress${taskUrlSuffix}`,
            method: taskMethod,
            body: task.id ? { id: task.id, ...task } : task,
          },
          {
            url: `/Project/${projectId}/tasks${taskUrlSuffix}`,
            method: taskMethod,
            body: task.id ? { id: task.id, ...task } : task,
          },
          {
            url: `/ProjectProgress/${projectId}${taskUrlSuffix}`,
            method: taskMethod,
            body: task.id ? { id: task.id, ...task } : { projectId, ...task },
          },
          {
            url: `/Project/${projectId}`,
            method: "PUT",
            body: {
              id: projectId,
              ...buildProjectPayload({
                ...projectSnapshot,
                progressTasks: task.id
                  ? projectSnapshot.progressTasks.map((item) =>
                      item.id === task.id ? { ...item, ...task, id: task.id } : item
                    )
                  : [{ id: `TSK-${Date.now()}`, ...task }, ...projectSnapshot.progressTasks],
              }),
            },
          },
        ]);

        if (result.error) {
          return { error: result.error };
        }

        return {
          data: (result.data ?? {
            success: true,
            message: "Task progress saved successfully.",
            data: task,
            errors: [],
          }) as ProjectApiResponse,
        };
      },
      invalidatesTags: (_result, _error, arg) => ["Project", { type: "Project", id: arg.projectId }],
    }),
    deleteProjectTask: builder.mutation<ProjectApiResponse, DeleteProjectTaskArgs>({
      async queryFn({ projectId, taskId, projectSnapshot }, _api, _extraOptions, baseQuery) {
        const { result } = await attemptQueryChain(baseQuery, [
          { url: `/Project/${projectId}/progress/${taskId}`, method: "DELETE" },
          { url: `/Project/${projectId}/tasks/${taskId}`, method: "DELETE" },
          { url: `/ProjectProgress/${taskId}`, method: "DELETE" },
          {
            url: `/Project/${projectId}`,
            method: "PUT",
            body: {
              id: projectId,
              ...buildProjectPayload({
                ...projectSnapshot,
                progressTasks: projectSnapshot.progressTasks.filter((task) => task.id !== taskId),
              }),
            },
          },
        ]);

        if (result.error) {
          return { error: result.error };
        }

        return {
          data: (result.data ?? {
            success: true,
            message: "Task progress deleted successfully.",
            data: taskId,
            errors: [],
          }) as ProjectApiResponse,
        };
      },
      invalidatesTags: (_result, _error, arg) => ["Project", { type: "Project", id: arg.projectId }],
    }),
    deleteProject: builder.mutation<ProjectApiResponse, string>({
      query: (id) => ({
        url: `/Project/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Project"],
    }),

    fetchProjectContracts: builder.query<ProjectContractFile[], string>({
      query: (projectId) => ({ url: `/Project/${projectId}/contracts`, method: "GET" }),
      transformResponse: (response: ProjectApiResponse<unknown> | unknown) => {
        const data = (response as ProjectApiResponse<unknown>)?.data ?? response;
        if (Array.isArray(data)) {
          return data.map((item: Record<string, any>) => ({
            id: Number(item.id ?? item.fileId ?? 0),
            fileName: String(item.fileName ?? item.name ?? item.file ?? ""),
            fileUrl: String(item.fileUrl ?? item.url ?? item.filePath ?? ""),
            uploadedAt: item.uploadedAt ? String(item.uploadedAt) : undefined,
          }));
        }
        return [];
      },
      providesTags: (_result, _error, projectId) => [{ type: "Project", id: projectId }],
    }),

    uploadProjectContract: builder.mutation<void, { projectId: string; file: File }>({
      query: ({ projectId, file }) => {
        const formData = new FormData();
        formData.append("file", file);
        return { url: `/Project/${projectId}/contracts`, method: "POST", body: formData };
      },
      invalidatesTags: (_result, _error, { projectId }) => [
        { type: "Project", id: projectId },
        "Project",
      ],
    }),

    deleteProjectContract: builder.mutation<void, { projectId: string; fileId: number }>({
      query: ({ projectId, fileId }) => ({
        url: `/Project/${projectId}/contracts/${fileId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { projectId }) => [{ type: "Project", id: projectId }],
    }),

    fetchClientProjectsForAdmin: builder.query<ProjectRecord[], string>({
      query: (clientId) => ({ url: `/Client/${clientId}/projects`, method: "GET" }),
      transformResponse: (response: ProjectApiResponse<unknown> | unknown) =>
        getProjectItems(response).map(normalizeProjectRecord),
      providesTags: (_result, _error, clientId) => [
        { type: "ClientPermission" as const, id: clientId },
      ],
    }),
  }),
});

export const {
  useCreateProjectMutation,
  useDeleteProjectMutation,
  useDeleteProjectTaskMutation,
  useFetchClientProjectsQuery,
  useFetchClientProjectsForAdminQuery,
  useFetchProjectByIdQuery,
  useFetchProjectsQuery,
  useSaveProjectTaskMutation,
  useUpdateProjectMutation,
  useFetchProjectContractsQuery,
  useUploadProjectContractMutation,
  useDeleteProjectContractMutation,
} = projectsApi;


